/**
 * Pipeline Bottleneck Radar — what a signal is, and how a figure becomes a colour.
 *
 * Split out of `bottleneckRadar.ts` at the 500-line ceiling. The boundary is a real one rather
 * than a convenient cut: everything here is the VOCABULARY (a status, a threshold, a cohort too
 * thin to judge), and everything left behind is the four phases built out of it. Re-exported from
 * `bottleneckRadar.ts`, so no importer has to move.
 *
 * ⚠️ The re-export needs a matching `import` over there — `export *` does not bind these names in
 * the re-exporting module's own scope, and it calls `worst`, `ratioStatus`, `fractionStatus`,
 * `conversionSignal` and the formatters itself. Same trap `rolePrioritisationTeam.ts` records.
 */
import type { ConversionMeasure } from '../types/taBottleneck';

/** Qualified candidates wanted per open hire before a pipeline counts as full. */
export const PIPELINE_MULTIPLIER = 6;

/**
 * A role open this many weeks or fewer is ramping up: its pipeline has not had a full cycle, so red
 * is softened to yellow. Green and yellow are untouched — the softening only ever forgives.
 */
export const RAMP_WEEKS = 4;

/**
 * Below this many candidates a conversion rate is not a rate, it is an anecdote. Ten is the same
 * floor the mockup's own notes used for falling back off a role's figures.
 */
export const MIN_COHORT = 10;

/** Weeks the back-calculated interview targets assume a role gets to land its open hires. */
export const TARGET_FILL_WEEKS = 12;

export type SignalStatus = 'green' | 'yellow' | 'red' | 'unknown' | 'info';
export type StageStatus = 'green' | 'yellow' | 'red' | 'unknown';

/**
 * A signal's identity, separate from its display label.
 *
 * `actionsFor` has to find the coverage signal, the three conversions and so on, and matching on
 * `label` would tie what the radar RECOMMENDS to what it happens to print — rename a label for
 * layout and the advice silently stops being given. Only the signals something reads by name carry
 * one; the duration rows are display-only.
 */
export type SignalKey =
  | 'coverage'
  | 'conv1'
  | 'conv2'
  | 'conv3'
  | 'throughput2'
  | 'throughput3'
  | 'acceptance';

export interface Signal {
  label: string;
  value: string;
  sub: string;
  status: SignalStatus;
  key?: SignalKey;
}

export interface Stage {
  key: 's1' | 's2' | 's3' | 's4';
  name: string;
  status: StageStatus;
  signals: Signal[];
  /** True when this phase's red was softened because the role is still ramping up. */
  ramped?: boolean;
}

export interface RoleEvaluation {
  stages: Stage[];
  overall: StageStatus;
  bottleneck: Stage | null;
  ramp: boolean;
}

const RANK: Record<StageStatus, number> = { unknown: -1, green: 0, yellow: 1, red: 2 };

/** Worst of the real statuses. `info` and `unknown` never win — they are not verdicts. */
export function worst(statuses: SignalStatus[]): StageStatus {
  const real = statuses.filter((s): s is Exclude<StageStatus, 'unknown'> =>
    s === 'green' || s === 'yellow' || s === 'red'
  );
  if (!real.length) return 'unknown';
  return real.reduce<StageStatus>((a, b) => (RANK[b] > RANK[a] ? b : a), 'green');
}

/** A ratio of actual to expected. 1.0 is "behaving like FINN behaves". */
export function ratioStatus(ratio: number | null): StageStatus {
  if (ratio == null || !Number.isFinite(ratio)) return 'unknown';
  if (ratio >= 1) return 'green';
  if (ratio >= 0.8) return 'yellow';
  return 'red';
}

/** Coverage and throughput share a shape: a fraction of a target, forgiving down to 60%. */
export function fractionStatus(fraction: number | null): StageStatus {
  if (fraction == null || !Number.isFinite(fraction)) return 'unknown';
  if (fraction >= 1) return 'green';
  if (fraction >= 0.6) return 'yellow';
  return 'red';
}

export const pct = (v: number | null): string => (v == null ? '–' : `${Math.round(v)}%`);
export const num = (v: number | null): string => (v == null ? '–' : String(v));
export const days = (v: number | null): string => (v == null ? '–' : `${v}d`);

/** A wider cohort to judge on when the primary one is too thin. See `conversionSignal`. */
export interface ConversionFallback {
  actual: ConversionMeasure | undefined;
  expected: number | null;
  /** What that wider span is called on the card, e.g. "year to date". */
  note: string;
}

/**
 * A conversion against its benchmark, refusing to judge a cohort under `MIN_COHORT`.
 * Returns the status AND the sub-label, because "why is this grey" has to be readable on the card.
 *
 * ── The thin-cohort fallback ─────────────────────────────────────────────────────────────────
 * The mockup's own note prescribed "<10 cases → YTD" and only offer acceptance did it. Interviews
 * are rare per role, so at a narrow window most open postings have no judgeable case cohort — and
 * greying out phase 3 on a role that has run fifteen cases this year says nothing when the data to
 * say something exists. ⚠️ TWO GUARDS, neither belt-and-braces:
 *
 * 1. **The fallback must be BIGGER**, never merely different. The window is adjustable
 *    (90 / 180 / 365 / all history) and the year to date is only sometimes the wider span — at 365
 *    days or all history it is NARROWER, and falling back there would shrink the very cohort the
 *    reader widened the window to get. Comparing the two `n`s settles it with no calendar
 *    arithmetic about what month it happens to be.
 * 2. **The fallback carries its OWN benchmark.** Keeping the windowed one would put back exactly
 *    the two-different-spans comparison fixed in Oct 2026, where nobody would look for it.
 */
export function conversionSignal(
  label: string,
  actual: ConversionMeasure,
  expected: number | null,
  transition: string,
  /** Names the span both sides were measured over, e.g. "last 6 months". */
  windowNote?: string,
  fallback?: ConversionFallback
): Signal {
  const wider = fallback?.actual;
  const fellBack = actual.n < MIN_COHORT && !!wider && wider.n > actual.n && wider.n >= MIN_COHORT;
  const measure = fellBack ? (wider as ConversionMeasure) : actual;
  const bench = fellBack ? (fallback as ConversionFallback).expected : expected;

  if (measure.n < MIN_COHORT) {
    /**
     * The span is named here too. "Only 4 to judge on" with no span reads as a dead end; "only 4
     * to judge on · last 3 months" is the same fact plus the thing the reader can do about it,
     * which is the control directly above the card. (CodeRabbit, PR #463.)
     */
    return {
      label,
      value: measure.rate == null ? '–' : pct(measure.rate),
      sub: `${transition} · only ${measure.n} to judge on${windowNote ? ` · ${windowNote}` : ''}`,
      status: 'unknown',
    };
  }

  /**
   * The span is named because the role's rate and FINN's are measured over the SAME one — a reader
   * comparing two percentages is entitled to know which span produced both, and they used to come
   * from different ones. On a fallback it also says what the window held, so the figure on screen
   * can be reconciled with the control above it rather than looking like the control was ignored.
   */
  const span = fellBack
    ? `${(fallback as ConversionFallback).note}${
        windowNote ? ` · only ${actual.n} in the ${windowNote}` : ''
      }`
    : windowNote;

  const ratio = bench && bench > 0 && measure.rate != null ? measure.rate / bench : null;
  return {
    label,
    value: pct(measure.rate),
    sub: `${transition} · FINN ${pct(bench)} · n=${measure.n}${span ? ` · ${span}` : ''}`,
    status: ratioStatus(ratio),
  };
}
